import {
    Component,
    ElementRef,
    inject,
    input,
    output,
    OnInit,
    viewChild,
    computed,
    linkedSignal,
    OnDestroy,
} from '@angular/core';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatButtonToggleChange, MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { PageEvent } from '@angular/material/paginator';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import { Subject, takeUntil } from 'rxjs';
import {
    SearchStore,
    FilterKeys,
} from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { EventsStore } from '@app/stores/eventsStore';
import {
    ViewType,
    ViewToggleConfig,
    ViewHeaderAction,
} from '@app/shared/models/view-config.interface';
import { ViewConfigurationService } from '@app/shared/services/view-config.service';
import { SelectableItem } from '@app/stores/selectionStore';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass } from '@angular/common';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

@Component({
    selector: 'app-paginated-table-filter',
    templateUrl: './paginated-table-filter.component.html',
    styleUrls: ['./paginated-table-filter.component.scss'],
    imports: [
        MatFormField,
        MatLabel,
        MatIcon,
        MatPrefix,
        MatInput,
        MatSuffix,
        MatMenuTrigger,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatButton,
        MatTooltip,
        MatCheckbox,
        NgClass,
        MatMenu,
        MatRadioButton,
        MatRadioGroup,
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
    ],
})
export class PaginatedTableFilterComponent implements OnInit, OnDestroy {
    private appStore = inject(AppStore);
    private searchStore = inject(SearchStore);
    private eventsStore = inject(EventsStore);
    filterSearch = viewChild<ElementRef<HTMLInputElement>>('filterSearch');
    menuTrigger = viewChild(MatMenuTrigger);

    keysForFilteringObj = input<FilterKeys[]>([]);
    showClosedLabelInput = input<string>('OUTGOING-GOODS.SHOW-CLOSED-NOTES', {
        alias: 'showClosedLabel',
    });
    // Writable, seeded by the input; internal view-type changes update it
    // while parent binding changes re-seed it.
    showClosedLabel = linkedSignal(() => this.showClosedLabelInput());
    totalItems = input<number>(0);
    userRole = input<'consignor' | 'consignee' | 'carrier' | 'admin'>(
        'consignor'
    );
    shouldShowLocationFilter = input<boolean>(false);
    shouldShowColumnFilter = input<boolean>(false);
    filteredLocationsCount = input<number>(0);
    filteredColumnsCount = input<number>(0);
    isLocationFilterActive = input<boolean>(false);
    headerActions = input<ViewHeaderAction[]>([]);
    currentSelection = input<SelectableItem[]>([]);

    locationFilterClicked = output<void>();
    columnFilterClicked = output<void>();
    headerActionClicked = output<string>();

    // Local component state
    _keysForFilteringObj: FilterKeys[] = [];
    isStatus: boolean = false;
    isDiscrepancies: boolean = false;
    isPalletTickets: boolean = false;
    viewType = 'folder';

    // Notifications state — derived from EventsPaginatedService signal via getter
    private destroy$ = new Subject<void>();

    // Signals for reactive state
    currentViewType = this.searchStore.currentViewType;

    // Computed values from search state
    searchText = this.searchStore.searchText;
    selectedFilterKey = this.searchStore.selectedFilterKey;
    showClosed = computed(() => this.appStore.showClosed());
    loading = this.searchStore.loading;

    // Computed values for view switching
    viewToggleConfigComputed = computed(() => {
        return this.viewConfigService.getViewToggleConfig(this.userRole());
    });

    availableViews = computed(() => {
        const config = this.viewToggleConfigComputed();
        return (config.views || []).filter(view => {
            if (view.visible === false) return false;
            if (typeof view.visible === 'function') {
                return view.visible(); // Call the function to determine visibility
            }
            return view.visible == null || view.visible === true; // Default to true if not specified
        });
    });

    shouldShowViewToggle = computed(() => {
        return this.availableViews().length > 1;
    });
    pagination = this.searchStore.pagination;

    // Computed pagination values
    currentPage = computed(
        () => Math.floor(this.pagination().offset / this.pagination().limit) + 1
    );
    totalPages = computed(() =>
        Math.ceil(this.totalItems() / this.pagination().limit)
    );

    statuses: FilterKeys[] = [
        {
            keyName: 'open',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.OPEN',
        },
        {
            keyName: 'sent',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.SENT',
        },
        {
            keyName: 'closed',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.CLOSED',
        },
    ];

    selectedStatus: string = '';
    selectedDiscrepancies: string = '';
    selectedPalletTickets: string = '';

    boolStatus: FilterKeys[] = [
        {
            keyName: 'true',
            label: 'TRUE',
        },
        {
            keyName: 'false',
            label: 'FALSE',
        },
    ];

    constructor(
        private translate: TranslateService,
        private sessionService: SessionService,
        private viewConfigService: ViewConfigurationService,
        private eventsPaginatedService: EventsPaginatedService
    ) {}

    ngOnInit(): void {
        this._keysForFilteringObj = this.keysForFilteringObj();
    }

    /** Reads the newNotifications signal from eventsStore. */
    get hasNewNotifications(): boolean {
        return this.eventsStore.newNotifications();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onFilterChanges(filterKey: FilterKeys): void {
        this.searchStore.updateSelectedFilterKey(filterKey);
        this.searchStore.updateSearchText('');
        this.closeFilterMenu();
        this._keysForFilteringObj = [filterKey];
        this.isStatus = false;
        this.selectedStatus = '';
    }

    onStatusFilterChanges(filterKey: FilterKeys): void {
        let statusKey: FilterKeys;

        if (
            this.keysForFilteringObj().some(
                key => key.keyName === 'deliveryNoteBundleStatus'
            )
        ) {
            statusKey = {
                keyName: 'deliveryNoteBundleStatus',
                label: 'STATUS.STATE',
            };
        } else {
            statusKey = { keyName: 'status', label: 'STATUS.STATE' };
        }

        this._keysForFilteringObj = [statusKey];
        this.searchStore.updateSelectedFilterKey(statusKey);
        this.searchStore.updateSearchText(filterKey.keyName); // Store actual status value (open/closed/sent)
        this.isStatus = true;
        this.isDiscrepancies = false;
        this.isPalletTickets = false;
        this.selectedStatus = filterKey.keyName;
    }

    onPalletFilterChanges(filterKey: FilterKeys): void {
        const palletKey: FilterKeys = {
            keyName: 'palletTickets',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET',
        };
        this.isDiscrepancies = false;
        this.isStatus = false;
        this.isPalletTickets = true;
        this._keysForFilteringObj = [palletKey];
        this.searchStore.updateSelectedFilterKey(palletKey);
        this.searchStore.updateSearchText(
            this.translate.instant(filterKey.keyName.toUpperCase())
        );
    }

    onDiscrepanciesFilterChanges(filterKey: FilterKeys): void {
        const discrepanciesKey: FilterKeys = {
            keyName: 'discrepancies',
            label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
        };

        this._keysForFilteringObj = [discrepanciesKey];
        this.isDiscrepancies = true;
        this.isStatus = false;
        this.isPalletTickets = false;
        this.searchStore.updateSelectedFilterKey(discrepanciesKey);
        this.searchStore.updateSearchText(
            this.translate.instant(filterKey.keyName.toUpperCase())
        );
    }

    onSearchTextChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const text = input.value;
        this.updateSearchText(text);
    }

    private updateSearchText(text: string): void {
        // Filter out session email address to prevent accidental search
        const userSession = this.sessionService.getSession();
        if (text === userSession?.emailAddress) {
            this.resetFilter();
            return;
        }

        this.searchStore.updateSearchText(text);
    }

    onShowClosedChange(showClosed: boolean): void {
        this.appStore.setShowClosed(showClosed);

        // Trigger data refresh to reload with new filter
        this.searchStore.triggerDataRefresh();
    }

    resetFilter(): void {
        this.searchStore.updateSearchText('');
        this.searchStore.updateSelectedFilterKey(null);
        this._keysForFilteringObj = this.keysForFilteringObj();
        this.isStatus = false;
        this.isPalletTickets = false;
        this.isDiscrepancies = false;
        this.selectedStatus = '';
        this.selectedDiscrepancies = '';
        this.selectedPalletTickets = '';
        this.closeFilterMenu();
    }

    closeFilterMenu(): void {
        this.menuTrigger()?.closeMenu();
    }

    clearSearchInput(): void {
        if (!this.isStatus && !this.isDiscrepancies && !this.isPalletTickets) {
            if (this.filterSearch()?.nativeElement) {
                this.filterSearch()!.nativeElement.value = '';
            }
            this.updateSearchText('');
        }
    }

    getDisplayValue(): string {
        const searchText = this.searchText();

        // If filtering by status, show translated text
        if (this.isStatus && searchText) {
            const statusTranslationMap: { [key: string]: string } = {
                open: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.OPEN',
                sent: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.SENT',
                closed: 'OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.CLOSED',
            };

            const translationKey = statusTranslationMap[searchText];
            if (translationKey) {
                return this.translate.instant(translationKey);
            }
        }

        // For all other cases, return the raw search text
        return searchText;
    }

    // View type switching
    onViewTypeChange(event: MatButtonToggleChange): void {
        const newViewType = event.value as ViewType;

        // Get the search config for the new view type
        const viewConfig = this.viewConfigService.getViewConfig(
            this.userRole(),
            newViewType
        );
        const searchConfig = viewConfig?.searchConfig;
        this._keysForFilteringObj = [];

        // Clear notifications when switching to notifications view
        if (newViewType === 'notifications') {
            this.eventsPaginatedService.setNewNotificationsToFalse();
        }

        // update show-close label
        switch (newViewType) {
            case 'folders':
                this.showClosedLabel.set('OUTGOING-GOODS.SHOW-CLOSED');
                break;
            case 'deliverynotes':
                this.showClosedLabel.set('OUTGOING-GOODS.SHOW-CLOSED-NOTES');
                break;
            case 'tours':
                this.showClosedLabel.set('SPEDITEUR.TABLE.SHOW-CLOSED');
                break;
        }

        // reset all boolean filters
        this.isStatus = false;
        this.selectedStatus = '';

        this.isPalletTickets = false;
        this.selectedPalletTickets = '';

        this.isDiscrepancies = false;
        this.selectedDiscrepancies = '';

        this.searchStore.updateCurrentViewType(newViewType, searchConfig);
    }

    // Pagination methods
    onPageChange(event: PageEvent): void {
        const offset = event.pageIndex * event.pageSize;
        this.searchStore.updatePagination({ limit: event.pageSize, offset });
    }

    // Location filter methods
    onLocationFilterClick(): void {
        this.locationFilterClicked.emit();
    }

    isFilteringLocations(): boolean {
        return this.isLocationFilterActive();
    }

    getFilteredLocationsCount(): number {
        return this.filteredLocationsCount();
    }

    // Column filter methods
    onColumnFilterClick(): void {
        this.columnFilterClicked.emit();
    }

    // Action button methods
    onHeaderActionClick(actionId: string): void {
        this.headerActionClicked.emit(actionId);
    }

    isHeaderActionVisible(actionId: string): boolean {
        const action = this.headerActions().find(a => a.id === actionId);
        if (!action) return false;
        return action.visible
            ? action.visible(undefined, this.currentSelection())
            : true;
    }

    isHeaderActionDisabled(actionId: string): boolean {
        const action = this.headerActions().find(a => a.id === actionId);
        if (!action) return true;
        return action.disabled
            ? action.disabled(undefined, this.currentSelection())
            : false;
    }
}
